import * as React from 'react'

import { Box, Button, Touchable } from '@factorialco/deprecated-design-system'
import { Select } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

interface DeviceFormProps {
  selectedAssetId: string
  onAssetSelect: (assetId: string) => void
  onSave: () => void
  onCancel: () => void
  isEditing?: boolean
  usedAssetIds: Set<string>
  itAssets: Array<{
    id: string
    serialNumber: string
    itAssetModel?: {
      id: string
      brand: string
      name: string
      typeName: string
    } | null
  }>
}

export const DeviceForm: React.FC<DeviceFormProps> = ({
  selectedAssetId,
  onAssetSelect,
  onSave,
  onCancel,
  isEditing = false,
  usedAssetIds,
  itAssets,
}) => {
  return (
    <Box flexDirection="column" gap="s16">
      <Select
        label={''}
        placeholder={i18n.t('add_employee_modal.devices_step.form.device.placeholder')}
        showSearchBox
        searchBoxPlaceholder={i18n.t(
          'add_employee_modal.devices_step.form.device.search_placeholder'
        )}
        searchEmptyMessage={i18n.t('add_employee_modal.devices_step.form.device.no_results')}
        value={selectedAssetId || undefined}
        clearable
        searchFn={(asset, search) => {
          const term = search?.trim().toLowerCase() || ''

          if (term.length === 0 || asset.type !== 'item') {
            return true
          }
          const isItem = (itAsset: unknown): itAsset is (typeof itAssets)[number] => {
            return (
              typeof itAsset === 'object' &&
              itAsset !== null &&
              'id' in itAsset &&
              'serialNumber' in itAsset &&
              'itAssetModel' in itAsset
            )
          }

          if (!isItem(asset.item)) {
            return false
          }
          const item = asset.item

          const brand = item.itAssetModel?.brand || ''
          const model = item.itAssetModel?.name || ''
          const typeName = item.itAssetModel?.typeName || ''
          const serialNumber = item.serialNumber || ''
          const haystack = `${brand} ${model} ${typeName} ${serialNumber}`.toLowerCase()
          return haystack.includes(term)
        }}
        onChange={(value) => onAssetSelect(value || '')}
        options={itAssets
          .filter((asset) => {
            const isUsed = usedAssetIds.has(asset.id)
            const isCurrentlySelected = isEditing && selectedAssetId === asset.id
            return !isUsed || isCurrentlySelected
          })
          .map((asset) => {
            const brand = asset.itAssetModel?.brand || ''
            const model = asset.itAssetModel?.name || ''
            const label = `${brand} ${model}`.trim() || 'Unknown Device'
            const typeName = asset.itAssetModel?.typeName || 'Unknown'
            const serialNumber = asset.serialNumber || 'No Serial'
            return {
              type: 'item' as const,
              value: asset.id,
              label,
              description: `${typeName} • ${serialNumber}`,
              item: asset,
            }
          })}
      />

      <Box alignItems="center" justifyContent="flexEnd" flexDirection="row" gap="s12">
        <Touchable
          onClick={() => {
            onCancel()
          }}
        >
          <Button.Decorative type="secondary">
            {i18n.t('add_employee_modal.devices_step.form.buttons.cancel')}
          </Button.Decorative>
        </Touchable>

        <Touchable
          onClick={() => {
            onSave()
          }}
        >
          <Button.Decorative>
            {isEditing
              ? i18n.t('add_employee_modal.devices_step.form.buttons.update')
              : i18n.t('add_employee_modal.devices_step.form.buttons.save')}
          </Button.Decorative>
        </Touchable>
      </Box>
    </Box>
  )
}
